import {
  useQuery,
  useMutation,
  useQueryClient,
  UseMutationResult,
  UseQueryResult
} from 'react-query'
import {
  fetchProductPerformance,
  fetchPerformanceSummaries,
  fetchSecuritiesOwned,
  fetchTcAcceptance,
  acceptTcAcceptance,
  fetchVault,
  createVault,
  updateVault,
  activateVault,
  fetchMarketOrders,
  fetchHistoricalSpotPrices,
  ProductPerformance,
  ProductPerformanceSummary,
  SecurityOwned,
  TcAcceptance,
  Vault,
  VaultWrite,
  MarketOrder,
  HistoricalSpotPrice
} from 'api/inventory'

export function useProductPerformance(
  productId: number,
  vaultId?: number | null,
  options?: { enabled?: boolean }
): UseQueryResult<ProductPerformance> {
  return useQuery(
    ['product-performance', productId, vaultId ?? null],
    () => fetchProductPerformance(productId, vaultId),
    { staleTime: 300_000, enabled: options?.enabled ?? true }
  )
}

export function usePerformanceSummaries(
  vaultId?: number | null,
  options?: { enabled?: boolean }
): UseQueryResult<ProductPerformanceSummary[]> {
  return useQuery(
    ['performance-summaries', vaultId ?? null],
    () => fetchPerformanceSummaries(vaultId),
    {
      staleTime: 300_000,
      enabled: options?.enabled ?? true
    }
  )
}

export function useSecuritiesOwned(
  productId: number
): UseQueryResult<SecurityOwned[]> {
  return useQuery(
    ['securities-owned', productId],
    () => fetchSecuritiesOwned(productId),
    { staleTime: 60_000 }
  )
}

export function useTcAcceptance(
  productId: number
): UseQueryResult<TcAcceptance> {
  return useQuery(
    ['tc-acceptance', productId],
    () => fetchTcAcceptance(productId),
    { staleTime: 60_000 }
  )
}

export function useMutateAcceptTc(
  productId: number
): UseMutationResult<void, unknown, void> {
  const qc = useQueryClient()
  return useMutation(() => acceptTcAcceptance(productId), {
    onSuccess: () => {
      qc.invalidateQueries(['tc-acceptance', productId])
    }
  })
}

export function useVault(id: number | null): UseQueryResult<Vault> {
  return useQuery(['vaults', id], () => fetchVault(id as number), {
    staleTime: 60_000,
    enabled: id !== null
  })
}

export function useMutateCreateVault(): UseMutationResult<
  Vault,
  unknown,
  { name: string; email?: string; dateOfBirth?: string; relationship?: string }
> {
  const qc = useQueryClient()
  return useMutation(createVault, {
    onSuccess: data => {
      qc.invalidateQueries(['vaults', data.id])
    }
  })
}

export function useMutateUpdateVault(): UseMutationResult<
  Vault,
  unknown,
  { vaultId: number; data: VaultWrite }
> {
  const qc = useQueryClient()
  return useMutation(
    ({ vaultId, data }: { vaultId: number; data: VaultWrite }) =>
      updateVault(vaultId, data),
    {
      onSuccess: (data, { vaultId }) => {
        qc.invalidateQueries(['vaults', vaultId])
      }
    }
  )
}

export function useMutateActivateVault(): UseMutationResult<
  Vault,
  unknown,
  number
> {
  const qc = useQueryClient()
  return useMutation(activateVault, {
    onSuccess: data => {
      qc.invalidateQueries(['vaults', data.id])
      qc.invalidateQueries(['current-user'])
      qc.invalidateQueries(['product-performance'])
      qc.invalidateQueries(['performance-summaries'])
    }
  })
}

export function useMarketOrders(
  productId: number
): UseQueryResult<MarketOrder[]> {
  return useQuery(
    ['market-orders', productId],
    () => fetchMarketOrders(productId),
    { staleTime: 60_000 }
  )
}

export function useHistoricalSpotPrices(
  productId: number,
  timeframe: string
): UseQueryResult<HistoricalSpotPrice[]> {
  return useQuery(
    ['spot-price-history', productId, timeframe],
    () => fetchHistoricalSpotPrices(productId, timeframe),
    { staleTime: 300_000 }
  )
}
